import { useState, useEffect, useRef } from "react";
import { logClientError } from "../utils/logClientError";
import { buildAuthHeaders } from "../utils/apiAuth";
import { useAuthToken } from "../hooks/useAuthToken";
import {
  AlertBanner,
  PageTitle,
  Panel,
  btnPrimary,
  inputClass,
} from "../components/controlPlaneUi";

interface ChatMessage {
  id?: number;
  message: string;
  response: string;
  createdAt?: string;
}

interface HistoryEntry {
  role: string;
  content: string;
}

const API_URL = process.env.REACT_APP_API_URL || "";

const ChatPage: React.FC = () => {
  const getToken = useAuthToken();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const headers = await buildAuthHeaders(getToken);
      const res = await fetch(`${API_URL}/api/chat/history`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ChatMessage[] = await res.json();
      setMessages(data.reverse());
    } catch (err) {
      console.error("Error loading chat history:", err);
      const token = await getToken().catch(() => "");
      if (token) logClientError(token, "Failed to load chat history", "ChatPage", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setError(null);
    setLoading(true);

    const history: HistoryEntry[] = messages.slice(-10).flatMap((m) => [
      { role: "user", content: m.message },
      { role: "assistant", content: m.response },
    ]);

    try {
      const headers = await buildAuthHeaders(getToken);
      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage, history }),
        signal: AbortSignal.timeout(300_000),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw Object.assign(new Error(text || `HTTP ${res.status}`), { status: res.status });
      }

      const data: { reply: string } = await res.json();
      setMessages((prev) => [...prev, { message: userMessage, response: data.reply }]);
    } catch (err: any) {
      console.error("Error sending message:", err);
      const detail = err?.message || "";
      const isTimeout = err?.name === "TimeoutError" || err?.status === 504;
      setError(
        isTimeout
          ? "Response timed out — the AI model is slow on CPU. Please try again."
          : `Failed to send message. ${detail}`
      );
      const token = await getToken().catch(() => "");
      if (token) logClientError(token, `Chat failed: ${detail}`, "ChatPage", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (historyLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C9A260]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <PageTitle kind="Chat" name="wiki-chat">
        <span className="text-xs text-slate-500 font-mono">claude · haiku</span>
      </PageTitle>

      {error && <AlertBanner variant="error">{error}</AlertBanner>}

      <Panel
        title="Messages"
        className="flex-1 flex flex-col min-h-0"
        action={
          <span className="text-[10px] font-mono text-slate-400">
            {messages.length} exchange{messages.length === 1 ? "" : "s"}
          </span>
        }
      >
        <div
          className="flex-1 overflow-y-auto space-y-4 mb-4"
          style={{ minHeight: "320px", maxHeight: "calc(100vh - 16rem)" }}
        >
          {messages.length === 0 && (
            <div className="text-center text-slate-500 py-12">
              <p className="text-sm font-mono">No messages yet</p>
              <p className="text-xs mt-1">Ask a question about the wiki below</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-end">
                <div
                  className="text-white px-3 py-2 rounded-sm max-w-[80%] text-sm"
                  style={{ backgroundColor: "#C9A260" }}
                >
                  <p className="whitespace-pre-wrap font-mono">{msg.message}</p>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-[#1a1917] border border-[#262420] text-[#E5E0D5] px-3 py-2 rounded-sm max-w-[80%] text-sm">
                  <p className="whitespace-pre-wrap">{msg.response}</p>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#1a1917] border border-[#262420] text-[#9D9585] px-3 py-2 rounded-sm text-sm font-mono">
                thinking…
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex gap-2 border-t border-[#262420] pt-4">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about the wiki…"
            rows={2}
            className={`${inputClass} !h-auto min-h-[2.25rem] resize-none flex-1`}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className={`${btnPrimary} self-end`}
          >
            Send
          </button>
        </div>
      </Panel>
    </div>
  );
};

export default ChatPage;
